import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WaterRequestService, BidRecord } from '../../../../services/water-request-service';
import { DialogService } from '../../../utility/dialog/dialog-service';
import { ModalType } from '../../../utility/dialog-template/dialog-template.component';
import { HttpService } from '../../../utility/http-service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';


@Component({
  selector: 'ws-request-form',
  templateUrl: './ws-request-form.component.html',
  standalone: false,
  styleUrls: ['./ws-request-form.component.css']
})
export class WaterRequestFormComponent implements OnInit {
  waterForm!: FormGroup;
  @Input() type!: 'Have' | 'Need';
  @Input() context!: 'Share' | 'Trade';
  @Input() recordToEdit?: BidRecord | null;

 
  // For Bidding Information (Trading Only)
  willingToPay: string = '';
  bidAmount: number | null = null;
  bidUnits: string = '';
  public rate?: number;

  qualityFields = ['tss', 'tds', 'chloride', 'barium', 'calciumcarbonate', 'iron', 'boron', 'hydrogensulfide', 'norm'];
  comparisonOptions = [
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' }
  ];
  id: string | undefined;
  createdByUserId: string | undefined;
  isEditMode = false;

  public sites: SiteInfo[] = [];
  public siteMap: { [key: string]: SiteInfo } = {};
  constructor(
    private fb: FormBuilder,
    private waterRequestService: WaterRequestService,
    private dialog: DialogService,
    private http: HttpService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: {
      recordId?: string;
      clientId?: string;
      type?: 'Have' | 'Need';
      context?: 'Share' | 'Trade';
    }
  ) { }


  ngOnInit(): void {
    this.http.get('Aquatrade/AvailableSites', (res: SiteInfo[]) => {
      this.sites = res;
      this.siteMap = res.reduce((acc, site) => {
        acc[site.name] = site;
        return acc;
      }, {} as { [key: string]: SiteInfo });
    });
    this.waterForm = this.fb.group({
      well_pad: ['newPad'],
      well_name: ['', Validators.required],
      latitude: ['', Validators.required],
      longitude: ['', Validators.required],
      can_accept_trucks: [false],
      can_accept_layflats: [false],
      start_date: ['', Validators.required],
      end_date: [{ value: '', disabled: true }, Validators.required], 
      rate_bpd: [0, [Validators.required, Validators.min(0)]],
      trucksCheckbox: [false],
      truck_transport_radius: [{ value: '', disabled: true }, Validators.min(0)],
      truck_capacity: [{ value: '', disabled: true }, Validators.min(0)],
      layflatsCheckbox: [false],
      layflats_transport_radius: [{ value: '', disabled: true }, Validators.min(0)],
      layflats_capacity: [{ value: '', disabled: true }, Validators.min(0)],
      // Quality fields
      tss_limit: [''], tss_measure_value: [''],
      tds_limit: [''], tds_measure_value: [''],
      chloride_limit: [''], chloride_measure_value: [''],
      barium_limit: [''], barium_measure_value: [''],
      calciumcarbonate_limit: [''], calciumcarbonate_measure_value: [''],
      iron_limit: [''], iron_measure_value: [''],
      boron_limit: [''], boron_measure_value: [''],
      hydrogensulfide_limit: [''], hydrogensulfide_measure_value: [''],
      norm_limit: [''], norm_measure_value: [''],
      // Bidding Information (only used for trading)
      bid_type: [''],
      bid_amount: [''],
      bid_units: ['']
    });

    // Enable end date when start date is selected
    this.waterForm.get('start_date')?.valueChanges.subscribe(startDate => {
      const endControl = this.waterForm.get('end_date');
      startDate ? endControl?.enable() : endControl?.disable();
    });

    // Enable truck fields
    this.waterForm.get('trucksCheckbox')?.valueChanges.subscribe(checked => {
      const radius = this.waterForm.get('truck_transport_radius');
      const capacity = this.waterForm.get('truck_capacity');

      if (checked) {
        radius?.enable();
        capacity?.enable();
      } else {
        radius?.disable();
        capacity?.disable();
      }
    });

    // Enable layflat fields
    this.waterForm.get('layflatsCheckbox')?.valueChanges.subscribe(checked => {
      const radius = this.waterForm.get('layflats_transport_radius');
      const capacity = this.waterForm.get('layflats_capacity');

      if (checked) {
        radius?.enable();
        capacity?.enable();
      } else {
        radius?.disable();
        capacity?.disable();
      }
    });

    // Use dialog data for new records
    if (this.data) {
      this.type = this.type ?? this.data.type ?? 'Have';
      this.context = this.data.context ?? this.context ?? 'Share';
    }

    // If editing, load record
    if (this.data?.recordId && this.data?.clientId) {
      this.isEditMode = true;
      this.loadEditRecord(); 
    } else {
      // Not edit mode: default context
      this.context = this.context ?? 'Share';
      this.type = this.type ?? 'Have';
    }

  }

  private loadEditRecord(): void {
    if (this.data?.recordId && this.data?.clientId) {
      this.isEditMode = true;

      this.waterRequestService.recordInfo(
        this.data.recordId,
        this.data.clientId,
        (record: BidRecord) => {
          // Patch the form once the record is returned
          this.patchForm(record);
          this.recordToEdit = record;
        }
      );
    }
  }

  private toDateInput(value?: Date | string | null): string | null {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime())
      ? null
      : d.toISOString().substring(0, 10);
  }

  patchForm(record: BidRecord) {
    [
      'end_date',
      'truck_transport_radius',
      'truck_capacity',
      'layflats_transport_radius',
      'layflats_capacity'
    ].forEach(c => this.waterForm.get(c)?.enable({ emitEvent: false }));
    this.context = record.mode;
    this.waterForm.patchValue({
      well_pad: record.siteEdit,
      well_name: record.siteName,
      latitude: record.latitude,
      longitude: record.longitude,

      can_accept_trucks: record.canAcceptTrucks,
      can_accept_layflats: record.canAcceptLayflats,

      start_date: this.toDateInput(record.startDate),
      end_date: this.toDateInput(record.endDate),

      rate_bpd: record.rate,

      trucksCheckbox: record.canProvideTrucks,
      truck_transport_radius: record.provideTrucksRange,
      truck_capacity: record.provideTrucksCapacity,

      layflatsCheckbox: record.canProvideLayflats,
      layflats_transport_radius: record.provideLayflatsRange,
      layflats_capacity: record.provideLayflatsCapacity,

      bid_type: record.willingToPay,
      bid_amount: record.bidAmount,
      bid_units: record.bidUnits,

      tss_limit: record.tssConditionType,
      tss_measure_value: record.tssValue,
      tds_limit: record.tdsConditionType,
      tds_measure_value: record.tdsValue,
      chloride_limit: record.chlorideConditionType,
      chloride_measure_value: record.chlorideValue,
      barium_limit: record.bariumConditionType,
      barium_measure_value: record.bariumValue,
      calciumcarbonate_limit: record.calciumCarbonateConditionType,
      calciumcarbonate_measure_value: record.calciumCarbonateValue,
      iron_limit: record.ironConditionType,
      iron_measure_value: record.ironValue,
      boron_limit: record.boronConditionType,
      boron_measure_value: record.boronValue,
      hydrogensulfide_limit: record.hydrogenSulfideConditionType,
      hydrogensulfide_measure_value: record.hydrogenSulfideValue,
      norm_limit: record.normConditionType,
      norm_measure_value: record.normValue
    }, { emitEvent: false });

    if (!record.startDate) {
      this.waterForm.get('end_date')?.disable({ emitEvent: false });
    }

    if (!record.canProvideTrucks) {
      this.waterForm.get('truck_transport_radius')?.disable({ emitEvent: false });
      this.waterForm.get('truck_capacity')?.disable({ emitEvent: false });
    }

    if (!record.canProvideLayflats) {
      this.waterForm.get('layflats_transport_radius')?.disable({ emitEvent: false });
      this.waterForm.get('layflats_capacity')?.disable({ emitEvent: false });
    }
  }


  formatDate(date?: Date | string | null): string | null {
    if (!date) return null;

    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${d.getFullYear()}-${month}-${day}`;
  }

  loadSiteInfo() {
    const sitePad = this.waterForm.get('well_pad')?.value;
    if (sitePad) {
      const site = this.siteMap[sitePad];
      if (site) {
        this.waterForm.patchValue({
          well_name: site.name,
          latitude: site.latitude,
          longitude: site.longitude
        });
      } else {
        this.waterForm.patchValue({
          well_name: '',
          latitude: '',
          longitude: ''
        });
      }
    }
  }

  public get calcBidTotal(): number {
    if (this.waterForm.value.rate_bpd && this.waterForm.value.bid_amount && this.waterForm.value.bid_units != '') {
      var multiplier = 1;
      if (this.waterForm.value.bid_units == 'USD/bbl.day') {
        multiplier = this.waterForm.value.rate_bpd;
      }
      return multiplier * this.waterForm.value.bid_amount;
    }
    return 0;
  }
  get calcBidSpecificTotal(): number {
    var bidTotal = this.calcBidTotal;
    if (this.waterForm.value.rate_bpd && bidTotal && this.waterForm.value.rate_bpd > 0)
      return bidTotal / this.waterForm.value.rate_bpd;
    return 0;
  }

  onSubmit(): void {
    if (!this.waterForm.valid) {
      this.waterForm.markAllAsTouched();
      this.dialog.openModal(
        'Invalid Form',
        'Please correct the highlighted fields before submitting.',
        ModalType.WARN
      );
      return;
    }

    const v = this.waterForm.getRawValue();

    const bidRecord: BidRecord = {
      mode: this.context === 'Trade' ? 'Trade' : 'Share',
      provideOrNeed: this.type === 'Have' ? 'Have' : 'Need',

      siteEdit: v.well_pad,
      siteName: v.well_name,

      latitude: Number(v.latitude),
      longitude: Number(v.longitude),

      startDate: new Date(v.start_date),
      endDate: new Date(v.end_date),
      rate: Number(v.rate_bpd),

      canAcceptTrucks: v.can_accept_trucks,
      canAcceptLayflats: v.can_accept_layflats,

      canProvideTrucks: v.trucksCheckbox,
      provideTrucksRange: v.truck_transport_radius
        ? Number(v.truck_transport_radius)
        : undefined,
      provideTrucksCapacity: v.truck_capacity
        ? Number(v.truck_capacity)
        : undefined,

      canProvideLayflats: v.layflatsCheckbox,
      provideLayflatsRange: v.layflats_transport_radius
        ? Number(v.layflats_transport_radius)
        : undefined,
      provideLayflatsCapacity: v.layflats_capacity
        ? Number(v.layflats_capacity)
        : undefined,

      willingToPay: v.bid_type, 
      bidAmount: v.bid_amount ? Number(v.bid_amount) : undefined,
      bidUnits: v.bid_units,

      tssConditionType: v.tss_limit,
      tssValue: v.tss_measure_value ? Number(v.tss_measure_value) : undefined,

      tdsConditionType: v.tds_limit,
      tdsValue: v.tds_measure_value ? Number(v.tds_measure_value) : undefined,

      chlorideConditionType: v.chloride_limit,
      chlorideValue: v.chloride_measure_value ? Number(v.chloride_measure_value) : undefined,

      bariumConditionType: v.barium_limit,
      bariumValue: v.barium_measure_value ? Number(v.barium_measure_value) : undefined,

      calciumCarbonateConditionType: v.calciumcarbonate_limit,
      calciumCarbonateValue: v.calciumcarbonate_measure_value
        ? Number(v.calciumcarbonate_measure_value)
        : undefined,

      ironConditionType: v.iron_limit,
      ironValue: v.iron_measure_value ? Number(v.iron_measure_value) : undefined,

      boronConditionType: v.boron_limit,
      boronValue: v.boron_measure_value ? Number(v.boron_measure_value) : undefined,

      hydrogenSulfideConditionType: v.hydrogensulfide_limit,
      hydrogenSulfideValue: v.hydrogensulfide_measure_value
        ? Number(v.hydrogensulfide_measure_value)
        : undefined,

      normConditionType: v.norm_limit,
      normValue: v.norm_measure_value ? Number(v.norm_measure_value) : undefined
    };

    console.log('Submitting bidRecord:', bidRecord);
    const actionText = this.isEditMode ? 'update' : 'create';

    this.dialog.openModal(
      'Confirm Submission',
      `Are you sure you want to ${actionText} this Aquatrade request?`,
      ModalType.QUESTION,
      () => {
        if (this.isEditMode && this.recordToEdit?.id) {
          const recordToSave = { ...bidRecord, id: this.recordToEdit.id } as any;
          recordToSave.clientId = this.recordToEdit?.clientId;
          this.waterRequestService.editRecord(recordToSave, () => {
            this.dialog.openModal(
              'Success',
              'Aquatrade request updated successfully.',
              ModalType.INFO
            );
            this.waterForm.reset();
            this.isEditMode = false;
          });
        } else {
          // Add new record
          this.waterRequestService.addRecord(bidRecord as any, () => {
            this.dialog.openModal(
              'Success',
              'Aquatrade request created successfully.',
              ModalType.INFO
            );
            this.waterForm.reset();
          });
        }
      }
    );
  }


}

export declare interface SiteInfo {
  name: string;
  latitude: number;
  longitude: number;
}
